import { DestroyRef, inject, Injectable, InjectionToken, Type } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SidebarService } from './sidebar.service';

export interface SidebarEntityHandler {
  entityType: string;
  component: Type<any>;
  inputKey: string;
}

export const SIDEBAR_ENTITY_HANDLERS = new InjectionToken<SidebarEntityHandler[]>(
  'SIDEBAR_ENTITY_HANDLERS'
);

@Injectable({ providedIn: 'root' })
export class SidebarDeepLinkService {
  private readonly router = inject(Router);
  private readonly sidebarService = inject(SidebarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly handlers = inject(SIDEBAR_ENTITY_HANDLERS, { optional: true }) ?? [];

  private readonly registry: Map<string, SidebarEntityHandler>;
  private initialized = false;
  private lastProcessedKey: string | null = null;
  private lastProcessedTime = 0;
  private readonly DEBOUNCE_MS = 1000;

  constructor() {
    const flatHandlers = ([] as SidebarEntityHandler[]).concat(...this.handlers);
    this.registry = new Map(
      flatHandlers.map(h => [h.entityType, h])
    );
    console.log('[SidebarDeepLinkService] Registered entity types:', this.getRegisteredEntityTypes());
    this.init();
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      if (this.hasOAuthFragment()) {
        console.log('[SidebarDeepLinkService] Skipping - OAuth callback in progress');
        return;
      }
      this.handleQueryParams();
    });
  }

  private hasOAuthFragment(): boolean {
    const hash = window.location.hash;
    return hash.includes('state=') || hash.includes('code=') || hash.includes('session_state=');
  }

  private handleQueryParams(): void {
    const params = new URLSearchParams(window.location.search);
    const showId = params.get('show');
    const entityType = params.get('type');

    if (!showId || !entityType) {
      if (this.lastProcessedKey) {
        console.log('[SidebarDeepLinkService] No deep link params, closing sidebar');
        this.lastProcessedKey = null;
        this.sidebarService.close();
      }
      return;
    }

    const deepLinkKey = `${entityType}:${showId}`;
    const now = Date.now();

    if (this.lastProcessedKey === deepLinkKey && (now - this.lastProcessedTime) < this.DEBOUNCE_MS) {
      console.log('[SidebarDeepLinkService] Debouncing rapid duplicate:', deepLinkKey);
      return;
    }

    const handler = this.registry.get(entityType);
    if (handler) {
      console.log('[SidebarDeepLinkService] Opening sidebar for:', deepLinkKey);
      this.lastProcessedKey = deepLinkKey;
      this.lastProcessedTime = now;
      this.sidebarService.open(handler.component, { [handler.inputKey]: showId });
    } else {
      console.warn(`[SidebarDeepLinkService] No handler registered for entity type: ${entityType}`);
    }
  }

  getRegisteredEntityTypes(): string[] {
    return Array.from(this.registry.keys());
  }
}
